<template>
    <div class="highlight">
        <img :src="highlightAssetUrl" @click="openHighlightPopup()" />
        <h5>{{ highlightHeadline }}</h5>
        <p>{{ highlightText }}</p>
        <div class="items">
            <div
                v-for="(item, index) in highlightUsps"
                :key="index"
                class="item"
            >
                <img :src="item.uspAssetUrl" />
                <p>{{ item.uspAssetText }}</p>
            </div>
        </div>
        <div class="content-mobile">
            <div class="image-wrapper">
                <img
                    :src="highlightMobileAssetUrl"
                    @click="openHighlightPopup()"
                />
            </div>
            <ol>
                <li v-for="(item, index) in highlightMobileList" :key="index">
                    {{ item.highlightMobileListItem }}
                </li>
            </ol>
        </div>
    </div>
    <PopUp id="popupHighlight" ref="popupHighlight">
        <div class="slider-wrapper">
            <button
                class="custom-swiper-prev"
                @click="swiper.slidePrev()"
            ></button>
            <Swiper
                :loop="false"
                :rewind="true"
                :slides-per-view="1"
                :space-between="0"
                :navigation="false"
                @swiper="onSwiper"
                @slideChange="onSlideChange"
            >
                <Swiper-slide
                    v-for="(slide, index) in highlightPopupSlides"
                    :key="index"
                    class="slide-content"
                >
                    <div class="image-wrapper">
                        <img
                            :src="slide.popupSlideAssetUrl"
                            alt="Slide Image"
                        />
                    </div>
                </Swiper-slide>
            </Swiper>
            <button
                class="custom-swiper-next"
                @click="swiper.slideNext()"
            ></button>
        </div>
        <div class="slider-pagination">
            <span v-for="(slide, index) in highlightPopupSlides" :key="index">
                <span
                    :class="{ active: index === activeSlide }"
                    @click="swiper.slideTo(index)"
                ></span>
            </span>
        </div>
    </PopUp>
</template>

<script>
import { ref } from "vue";
import PopUp from "./PopUp.vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

export default {
    name: "PopupHighlight",
    components: {
        PopUp,
        Swiper,
        SwiperSlide,
    },
    props: {
        highlightAssetUrl: {
            type: String,
            default: "",
            required: true,
        },
        highlightMobileAssetUrl: {
            type: String,
            default: "",
            required: true,
        },
        highlightHeadline: {
            type: String,
            default: "",
            required: true,
        },
        highlightText: {
            type: String,
            default: "",
            required: true,
        },
        highlightMobileList: {
            type: Array,
            default: () => [],
            required: true,
        },
        highlightUsps: {
            type: Array,
            default: () => [],
            required: true,
        },
        highlightPopupSlides: {
            ttype: Array,
            default: () => [],
            required: true,
        },
    },
    setup() {
        const swiper = ref();
        const activeSlide = ref(0);

        function onSwiper(_swiper) {
            swiper.value = _swiper;
        }

        function onSlideChange() {
            if (!swiper.value) {
                return;
            }

            activeSlide.value = swiper.value.realIndex;
        }

        return {
            swiper,
            onSwiper,
            onSlideChange,
            activeSlide,
        };
    },
    methods: {
        openHighlightPopup() {
            this.$refs.popupHighlight.openPopup();
        },
    },
};
</script>

<style lang="scss" scoped>
@use "@/assets/customizer.vars.scss" as *;
.highlight {
    display: grid;
    grid-template-columns: 156px 1fr;
    grid-template-rows: auto;
    column-gap: 16px;
    row-gap: 12px;
    width: 100%;
    padding: 32px;
    border: 1px solid $color-light-gray;
    border-radius: 32px;
    background: rgba(255, 255, 255, 0.5);

    @media (max-width: $breakpoint-tablet) {
        display: block;
        padding: 16px;
    }

    > img {
        grid-row: 1 / 4;
        width: 100%;
        border-radius: 10px;
        overflow: hidden;
        cursor: pointer;

        @media (max-width: $breakpoint-tablet) {
            display: none;
        }
    }

    > h5 {
        grid-column: 2;
        grid-row: 1;

        @media (max-width: $breakpoint-tablet) {
            display: none;
        }
    }

    > p {
        grid-column: 2;
        grid-row: 2;
        font-size: 16px;
        font-weight: 700;

        @media (max-width: $breakpoint-tablet) {
            display: none;
        }
    }

    .items {
        grid-column: 2;
        grid-row: 3;
        display: flex;
        flex-direction: row;
        align-items: center;
        column-gap: 8px;

        @media (max-width: $breakpoint-tablet) {
            display: none;
        }

        .item {
            display: flex;
            flex-direction: row;
            align-items: center;
            column-gap: 4px;
        }
    }

    .content-mobile {
        display: none;
        flex-direction: row;
        justify-content: center;
        column-gap: 16px;
        width: 100%;

        @media (max-width: $breakpoint-tablet) {
            display: flex;
        }

        .image-wrapper {
            display: flex;
            flex-direction: column;
            align-items: center;
            row-gap: 4px;
            border-radius: 10px;
            overflow: hidden;

            img {
                min-width: 102px;
                width: 100%;
                cursor: pointer;
            }
        }

        ol {
            display: flex;
            flex-direction: column;
            justify-content: center;
            min-width: 50%;
            padding-left: 16px;
            margin: 0;

            @media (max-width: 450px) {
                min-width: 40%;
            }
        }
    }
}

.slider-wrapper {
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    column-gap: 24px;
    width: 100%;

    .swiper-slide {
        align-self: center;

        .image-wrapper {
            display: flex;
            flex-direction: row;
            justify-content: center;
            width: 100%;
            height: 100%;

            img {
                @media (max-width: $breakpoint-mobile) {
                    width: calc(100% - 34px);
                    height: 100%;
                    object-fit: contain;
                }
            }
        }
    }

    .custom-swiper-prev,
    .custom-swiper-next {
        position: unset;
        display: flex;
        justify-content: center;
        align-items: center;
        min-width: 40px;
        min-height: 40px;
        width: 40px;
        height: 40px;
        padding: 5px;
        background-color: $color-new-black;
        border-radius: 50%;
        border: none;
        box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.05);
        z-index: 10;
        overflow: hidden;
        cursor: pointer;

        @media (max-width: $breakpoint-mobile) {
            position: absolute;
            width: 34px;
            height: 34px;
        }

        &::before {
            content: "";
            display: block;
            width: 100%;
            height: 100%;
            background-image: url("~@/assets/customizer_icon_slider_btn_left.svg");
            background-size: contain;
            background-position: center;
            background-repeat: no-repeat;
            background-clip: content-box;
        }
    }

    .custom-swiper-prev {
        @media (max-width: $breakpoint-mobile) {
            left: 0;
        }
    }

    .custom-swiper-next {
        transform: rotate(180deg);

        @media (max-width: $breakpoint-mobile) {
            right: 0;
        }
    }
}

.slider-pagination {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    column-gap: 5px;
    margin-top: 22px;

    span {
        display: block;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background-color: $color-light-gray;
        cursor: pointer;

        &:hover,
        &.active {
            background-color: $color-new-black;
        }
    }
}
</style>
